'use client';

import { Group } from '@visx/group';
import { LinePath } from '@visx/shape';
import { scaleLinear, scaleTime } from '@visx/scale';
import { min, max } from 'd3-array';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Series } from 'mode-shared/hooks/synth/usePricePredictions';
import { Selection, scaleSequential, interpolateRainbow, pointer, select } from 'd3';
import { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { CHART_CONFIG } from 'mode-shared/utils/graphs';
import { useResponsiveDimensions } from 'mode-shared/hooks/useResponsiveDimensions';
import { useVisxZoomDomainTimeSeries, TimeSeriesPoint } from 'mode-shared/hooks/useVisxZoomDomainTimeSeries';
import { ZoomControls } from 'mode-shared/components/insights/ZoomControls';
import { useResponsiveTicks } from 'mode-shared/hooks/useResponsiveTicks';
import { Zoom } from '@visx/zoom';
import { RectClipPath } from '@visx/clip-path';
import { abbreviateNumber } from 'mode-shared/utils/format';

interface ClosestSeries {
  series: Series;
  index: number;
  point: { time: Date; price: number };
  mouseX: number;
  mouseY: number;
}

const DIMENSIONS = {
  margin: { left: 50, bottom: 40, top: 10, right: 12 },
};

const formatTime = (date: Date) => date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

export const MinerPredictionsGraph = ({
  series,
  onSelectTime,
}: {
  series: Series[];
  onSelectTime: (time: Date | undefined) => void;
}) => {
  const [isLocked, setIsLocked] = useState(false);
  const [hoveredXValue, setHoveredXValue] = useState<number | null>(null);

  const { width, height } = useResponsiveDimensions({
    defaultWidth: 774,
    defaultHeight: 400,
    desiredRatio: 0.8,
    breakpoint: 1024,
  });
  const config = useMemo(() => ({ ...CHART_CONFIG, ...DIMENSIONS, width, height }), [width, height]);

  const innerWidth = config.width - config.margin.left - config.margin.right;
  const innerHeight = config.height - config.margin.top - config.margin.bottom;

  const svgRef = useRef<SVGSVGElement>(null);
  const verticalLineRef = useRef<SVGLineElement>(null);
  const mouseRectRef = useRef<SVGRectElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipSelectionRef = useRef<Selection<HTMLDivElement, unknown, null, undefined> | null>(null);

  const colors = useMemo(
    () =>
      scaleSequential()
        .domain([0, series?.length || 0])
        .interpolator(interpolateRainbow),
    [series]
  );

  // Prepare flat points array for zoom/y-domain logic
  const allPoints: TimeSeriesPoint[] = useMemo(
    () => series.flatMap((s) => s.values.map((d) => ({ x: d.time.getTime(), y: d.price }))),
    [series]
  );

  const initialXDomain = useMemo(() => {
    const times = allPoints.map((d) => d.x);
    return [Math.min(...times), Math.max(...times)] as [number, number];
  }, [allPoints]);

  const initialYDomain = useMemo(
    () =>
      [
        (min(series || [], (s) => min(s.values, (d) => d.price)) || 0) * 0.999,
        (max(series || [], (s) => max(s.values, (d) => d.price)) || 0) * 1.001,
      ] as [number, number],
    [series]
  );

  const { xDomain, yDomain, zoomRef } = useVisxZoomDomainTimeSeries({
    initialXDomain,
    initialYDomain,
    points: allPoints,
    innerWidth,
  });

  const scales = useMemo(
    () => ({
      x: scaleTime({
        domain: [new Date(xDomain[0]), new Date(xDomain[1])],
        range: [0, innerWidth],
      }),
      y: scaleLinear({
        domain: yDomain,
        range: [innerHeight, 0],
      }),
    }),
    [xDomain, yDomain, innerWidth, innerHeight]
  );

  const xNumTicks = useResponsiveTicks({ width: config.width });

  const findClosestPoint = useCallback(
    (adjustedMouseX: number, mouseY: number): ClosestSeries | null => {
      if (!series) return null;
      let closest: ClosestSeries | null = null;
      let minDistance = Infinity;
      series.forEach((s, i) => {
        s.values.forEach((d) => {
          const screenX = scales.x(d.time);
          const screenY = scales.y(d.price);
          const dx = adjustedMouseX - screenX;
          const dy = mouseY - screenY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < minDistance) {
            minDistance = distance;
            closest = {
              series: s,
              index: i,
              point: d,
              mouseX: screenX,
              mouseY: screenY,
            };
          }
        });
      });
      return closest;
    },
    [series, scales]
  );

  const getAdjustedMouseX = (mouseX: number) => {
    const { scaleX = 1, translateX = 0 } = zoomRef.current?.transformMatrix || {};
    return (mouseX - translateX) / scaleX;
  };

  useEffect(() => {
    if (tooltipRef.current) {
      tooltipSelectionRef.current = select<HTMLDivElement, unknown>(tooltipRef.current);
    }
    return () => {
      tooltipSelectionRef.current = null;
    };
  }, []);

  const updateVisualization = useCallback(
    (mouseX: number, closestSeries: ClosestSeries) => {
      if (!svgRef.current || !tooltipSelectionRef.current) return;
      const xValue = closestSeries.point.time.getTime();
      setHoveredXValue(xValue);
      const screenX = scales.x(closestSeries.point.time) + config.margin.left;
      tooltipSelectionRef.current.style('left', `${screenX - 90}px`).style('opacity', 1).html(`
        <div class="tooltip-content">
          <p>${formatTime(closestSeries.point.time)}</p>      
          <p>${isLocked ? 'Click to unlock' : 'Click to lock'}</p>
        </div>
      `);
    },
    [svgRef, tooltipSelectionRef, config.margin.left, isLocked, scales.x]
  );

  // Helper to check if zoom is at initial state (with tolerance)
  const isZoomInitial = () => {
    const { scaleX = 1 } = zoomRef.current?.transformMatrix || {};
    return scaleX === 1;
  };

  // Update handleMouseMove and handleClick to ignore selection if zoom is not initial
  const handleMouseMove = useCallback(
    (event: any) => {
      if (isLocked) return;
      const [mouseX, mouseY] = pointer(event);
      const adjustedMouseX = getAdjustedMouseX(mouseX);
      const closestSeries = findClosestPoint(adjustedMouseX, mouseY);
      if (closestSeries) {
        updateVisualization(mouseX, closestSeries);
        onSelectTime(closestSeries.point.time);
      }
    },
    [isLocked, findClosestPoint, updateVisualization, onSelectTime]
  );

  const handleClick = useCallback(
    (event: any) => {
      if (!isZoomInitial()) return;
      const [mouseX, mouseY] = pointer(event);
      const adjustedMouseX = getAdjustedMouseX(mouseX);
      if (isLocked) {
        setIsLocked(false);
        onSelectTime(undefined);
        setHoveredXValue(null);
      } else {
        const foundSeries = findClosestPoint(adjustedMouseX, mouseY);
        if (foundSeries) {
          setIsLocked(true);
          onSelectTime(foundSeries.point.time);
          setHoveredXValue(foundSeries.point.time.getTime());
        }
      }
    },
    [isLocked, findClosestPoint, onSelectTime]
  );

  useEffect(() => {
    mouseRectRef.current?.addEventListener('mousemove', handleMouseMove);
    mouseRectRef.current?.addEventListener('click', handleClick);
    return () => {
      mouseRectRef.current?.removeEventListener('mousemove', handleMouseMove);
      mouseRectRef.current?.removeEventListener('click', handleClick);
    };
  }, [handleMouseMove, handleClick]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const handler = (e: WheelEvent) => {
      zoomRef.current?.handleWheel?.(e as any);
    };
    svg.addEventListener('wheel', handler, { passive: false });
    return () => svg.removeEventListener('wheel', handler);
  }, [zoomRef]);

  // Reset selected timeline on zoom in
  useEffect(() => {
    if (!isZoomInitial() && (isLocked || hoveredXValue !== null)) {
      setIsLocked(false);
      setHoveredXValue(null);
      onSelectTime(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xDomain]);

  return (
    <div className="relative w-full">
      <Zoom
        width={config.width}
        height={config.height}
        scaleXMin={1}
        scaleXMax={10}
        scaleYMin={1}
        scaleYMax={10}
        initialTransformMatrix={{
          scaleX: 1,
          scaleY: 1,
          translateX: 0,
          translateY: 0,
          skewX: 0,
          skewY: 0,
        }}
      >
        {(zoom: any) => {
          zoomRef.current = zoom;
          return (
            <div className="relative select-none">
              <svg
                width={config.width}
                height={config.height}
                style={{ touchAction: 'none', cursor: zoom.isDragging ? 'grabbing' : 'grab' }}
                ref={svgRef}
                onMouseDown={zoom.dragStart}
                onMouseUp={zoom.dragEnd}
                onTouchStart={zoom.dragStart}
                onTouchEnd={zoom.dragEnd}
              >
                <RectClipPath id="zoom-clip" width={innerWidth} height={innerHeight} />
                <Group left={config.margin.left} top={config.margin.top}>
                  <Group clipPath="url(#zoom-clip)">
                    <GridRows
                      scale={scales.y}
                      width={innerWidth}
                      height={innerHeight}
                      stroke={config.colors.grid}
                      opacity={config.gridRows.opacity}
                      strokeWidth={config.gridRows.strokeWidth}
                    />
                    {/* Base layer - grayscale lines */}
                    <g style={{ opacity: 0.8, filter: 'grayscale(1)' }}>
                      {series.map((prediction, index) => (
                        <LinePath
                          key={`bw-${index}`}
                          data={prediction.values}
                          x={(d) => scales.x(d.time)}
                          y={(d) => scales.y(d.price)}
                          stroke={colors(index)}
                          strokeWidth={1}
                        />
                      ))}
                    </g>
                    {/* Overlay - colored lines with mask */}
                    <g>
                      {series.map((prediction, index) => (
                        <LinePath
                          key={index}
                          data={prediction.values}
                          x={(d) => scales.x(d.time)}
                          y={(d) => scales.y(d.price)}
                          stroke={colors(index)}
                          strokeWidth={1}
                          style={{
                            clipPath:
                              hoveredXValue !== null
                                ? `polygon(0 0, ${isLocked ? scales.x(new Date(hoveredXValue)) : config.width}px 0, ${isLocked ? scales.x(new Date(hoveredXValue)) : config.width}px 100%, 0 100%)`
                                : 'none',
                          }}
                        />
                      ))}
                    </g>
                  </Group>
                  {/* Vertical line for hover/lock */}
                  {hoveredXValue !== null && (
                    <line
                      x1={scales.x(new Date(hoveredXValue))}
                      x2={scales.x(new Date(hoveredXValue))}
                      y1={0}
                      y2={innerHeight}
                      stroke={config.colors.primary}
                      strokeWidth="1px"
                      opacity={1}
                    />
                  )}
                  <AxisLeft
                    scale={scales.y}
                    stroke={config.colors.axis}
                    tickStroke={config.colors.axis}
                    numTicks={10}
                    tickLabelProps={() => ({
                      fill: config.colors.axis,
                      fontSize: 11,
                      textAnchor: 'end',
                      dx: -4,
                    })}
                    tickFormat={(d) => `$${abbreviateNumber(Number(d))}`}
                  />
                  <AxisBottom
                    top={innerHeight}
                    scale={scales.x}
                    stroke={config.colors.axis}
                    tickStroke={config.colors.axis}
                    numTicks={xNumTicks}
                    tickFormat={(d) =>
                      new Date(d as Date).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                      })
                    }
                    tickLabelProps={() => ({
                      fill: config.colors.axis,
                      fontSize: 11,
                      textAnchor: 'middle',
                      dy: 4,
                    })}
                  />
                </Group>
                <line
                  ref={verticalLineRef}
                  y1={config.margin.top}
                  y2={config.height - config.margin.bottom}
                  stroke={config.colors.primary}
                  strokeWidth="1px"
                  opacity={0}
                />

                <rect
                  ref={mouseRectRef}
                  width={innerWidth}
                  height={innerHeight}
                  fill="none"
                  pointerEvents="all"
                  transform={`translate(${config.margin.left},${config.margin.top})`}
                />
              </svg>
              <ZoomControls className="top-0 right-3 flex-row" zoom={zoom} />
            </div>
          );
        }}
      </Zoom>

      <div
        ref={tooltipRef}
        className="text-xs absolute opacity-0 bg-grey-900 p-2 rounded text-neutral-400 pointer-events-none transition-opacity duration-200 top-[35px] w-[88px] text-right"
      />
    </div>
  );
};
